/**
 * Mapping between DOM `data-testid` values (already present on every SRC
 * template) and the underlying editable data path.
 *
 * The inline visual editor uses this table to translate a click on a
 * rendered element into a well-typed selection object. This means we do NOT
 * need to modify any SRC template file — every anchor point is a testid the
 * templates already ship with.
 *
 * Each entry has:
 *   • testid  — matches `data-testid` on the rendered element
 *   • type    — 'text' | 'image' | 'button' | a collection-editor type
 *   • path    — dot-path into `website_content` (or a callback for special
 *               cases like brand/summary keys)
 *   • label   — human-readable name shown in the right panel
 *   • section — optional grouping for the section outline
 *
 * The list is intentionally scoped to Phase-1 editable fields. Phase 2
 * will extend this with product-grid / menu / services entries.
 */

// (intentionally exported; referenced by findEditableAnchor below and by
//  future Phase 2 anchor additions)
export const EDITABLE_ANCHORS = [
  // ─── Service SRC hero ─────────────────────────────────────────────
  { testid: "hero-eyebrow",       type: "text",   path: "hero_eyebrow",   label: "Hero eyebrow",   section: "Hero" },
  { testid: "hero-title",         type: "text",   path: "hero_title",     label: "Hero headline",  section: "Hero" },
  { testid: "hero-subtitle",      type: "text",   path: "hero_subtitle",  label: "Hero subhead",   section: "Hero" },
  { testid: "hero-primary-cta",   type: "button", path: "hero_cta_text",  linkPath: "hero_cta_link", label: "Primary CTA", section: "Hero" },
  { testid: "hero-secondary-cta", type: "button", path: "hero_cta2_text", linkPath: "hero_cta2_link", label: "Secondary CTA", section: "Hero" },
  { testid: "hero-image",         type: "image",  path: "hero_image_url", label: "Hero image",     section: "Hero" },

  // ─── Ecommerce SRC hero ───────────────────────────────────────────
  { testid: "hero-headline",      type: "text",   path: "hero_title",     label: "Hero headline",  section: "Hero" },
  { testid: "hero-cta-primary",   type: "button", path: "hero_cta_text",  linkPath: "hero_cta_link", label: "Primary CTA", section: "Hero" },
  { testid: "hero-cta-secondary", type: "button", path: "hero_cta2_text", linkPath: "hero_cta2_link", label: "Secondary CTA", section: "Hero" },

  // ─── Restaurant hero ──────────────────────────────────────────────
  { testid: "restaurant-hero-title",    type: "text",  path: "hero_title",    label: "Restaurant headline", section: "Hero" },
  { testid: "restaurant-hero-subtitle", type: "text",  path: "hero_subtitle", label: "Restaurant subhead",  section: "Hero" },

  // ─── Phase 2 list anchors ─────────────────────────────────────────
  // Clicking one of these opens a dedicated collection editor in the right
  // panel — see RightPanel.jsx `ProductsEditor` / `MenuEditor` /
  // `ServicesEditor`.
  { testid: "product-grid",       type: "products",  path: null,          label: "Product grid",   section: "Products" },
  { testid: "featured-products",  type: "products",  path: null,          label: "Featured products", section: "Products" },
  { testid: "menu-preview",       type: "menu",      path: null,          label: "Menu",           section: "Menu" },
  { testid: "services-overview",  type: "services",  path: null,          label: "Services list",  section: "Services" },

  // ─── Phase 3 chrome anchors ────────────────────────────────────────
  // Clicking Header/Footer opens the dedicated site-chrome editors in the
  // right panel — HeaderEditor / FooterEditor. These have priority over
  // section-type anchors so that clicking anywhere in the header selects
  // "Header" instead of a nested link/button.
  { testid: "site-header",        type: "header",   path: null,          label: "Header",         section: "Header" },
  { testid: "site-footer",        type: "footer",   path: null,          label: "Footer",         section: "Footer" },

  // ─── Phase 3 section-type anchors ─────────────────────────────────
  // Clicking one of these opens the corresponding collection editor in
  // the right panel (TestimonialsEditor / FaqEditor / PricingEditor / etc).
  { testid: "testimonials-section", type: "testimonials", path: null, label: "Testimonials", section: "Testimonials" },
  { testid: "faq-section",          type: "faqs",         path: null, label: "FAQ",          section: "FAQ" },
  { testid: "pricing-section",      type: "pricing",      path: null, label: "Pricing",      section: "Pricing" },
  { testid: "team-section",         type: "team",         path: null, label: "Team",         section: "Team" },
  { testid: "gallery-section",      type: "gallery",      path: null, label: "Gallery",      section: "Gallery" },

  // ─── Iter 68: stale-anchor fixes ──────────────────────────────────
  // These SRC-template sections previously had no matching entry. They
  // open dedicated collection editors that PATCH straight into
  // `website_content`.
  //
  // • trust-bar        → TrustPointsEditor        (writes trust_points)
  // • why-choose-us    → WhyChooseUsEditor        (writes why_us)
  // • announcement-bar → AnnouncementMessagesEditor (writes announcement_messages)
  // • process-section  → ProcessStepsEditor       (writes process_steps)
  { testid: "trust-bar",         type: "trust_points",         path: null, label: "Trust Bar",         section: "Trust Bar" },
  { testid: "why-choose-us",     type: "why_choose_us",        path: null, label: "Why Choose Us",     section: "Why Choose Us" },
  { testid: "announcement-bar",  type: "announcement_messages", path: null, label: "Announcement Bar",  section: "Announcement Bar" },
  { testid: "process-section",   type: "process_steps",        path: null, label: "Process Steps",     section: "Process Steps" },

  // ─── Iter 75: section-heading text anchors ────────────────────────
  //
  // Two shapes here:
  //   1. Existing-scalar bindings — the heading already had a canonical
  //      `website_content.<field>` (from the ecommerce/service headline
  //      allowlist). Only the `data-testid` was missing on the DOM node
  //      + the entry here. `path` points to the scalar field.
  //   2. Copy-override bindings — the heading was previously
  //      derived/hardcoded with no store to persist edits. `copyPath`
  //      names a stable dot-notation key inside
  //      `website_content.copy_overrides`. `configPath` is the derived
  //      source in `publicPayload` so the editor loads the current
  //      visible value (default OR any saved override) when opened.
  //
  // Precedence rule (also mirrored on the backend): if a canonical scalar
  // exists we use it. A single heading MUST NOT be reachable through
  // both `path` and `copyPath`.

  // ── Service template ────────────────────────────────────────────────
  //   Home page — Services Overview
  { testid: "home-services-eyebrow",   type: "text",
    copyPath: "home.servicesOverview.eyebrow",
    configPath: "service_config.copy.home.servicesOverview.eyebrow",
    label: "Services Overview eyebrow", section: "Services Overview" },
  { testid: "home-services-title",     type: "text",
    copyPath: "home.servicesOverview.title",
    configPath: "service_config.copy.home.servicesOverview.title",
    label: "Services Overview headline", section: "Services Overview" },
  { testid: "home-services-subtitle",  type: "text",
    copyPath: "home.servicesOverview.subtitle",
    configPath: "service_config.copy.home.servicesOverview.subtitle",
    label: "Services Overview subhead", section: "Services Overview" },

  //   Home page — Consultation sell
  { testid: "home-consultation-eyebrow", type: "text",
    copyPath: "home.consultation.eyebrow",
    configPath: "service_config.copy.home.consultation.eyebrow",
    label: "Consultation eyebrow", section: "Consultation" },
  { testid: "home-consultation-title",   type: "text",
    copyPath: "home.consultation.title",
    configPath: "service_config.copy.home.consultation.title",
    label: "Consultation headline", section: "Consultation" },
  { testid: "home-consultation-subtitle", type: "text",
    copyPath: "home.consultation.subtitle",
    configPath: "service_config.copy.home.consultation.subtitle",
    label: "Consultation subhead", section: "Consultation" },

  //   Home page — Testimonials header
  { testid: "home-testimonials-eyebrow", type: "text",
    copyPath: "home.testimonials.eyebrow",
    configPath: "service_config.copy.home.testimonials.eyebrow",
    label: "Testimonials eyebrow", section: "Testimonials" },
  { testid: "home-testimonials-title",   type: "text",
    copyPath: "home.testimonials.title",
    configPath: "service_config.copy.home.testimonials.title",
    label: "Testimonials headline", section: "Testimonials" },

  //   Home page — Final CTA banner (CTABanner)
  { testid: "cta-title",    type: "text",
    copyPath: "home.finalCta.title",
    configPath: "service_config.copy.home.finalCta.title",
    label: "Final CTA headline", section: "Call to action" },
  { testid: "cta-subtitle", type: "text",
    copyPath: "home.finalCta.subtitle",
    configPath: "service_config.copy.home.finalCta.subtitle",
    label: "Final CTA subhead", section: "Call to action" },

  //   Home page — Why Choose Us component headline
  { testid: "why-choose-us-heading", type: "text",
    copyPath: "home.whyChooseUs.title",
    configPath: "service_config.copy.home.whyChooseUs.title",
    label: "Why Choose Us headline", section: "Why Choose Us" },

  //   Home page — Process Steps component headline
  { testid: "process-heading", type: "text",
    copyPath: "home.process.title",
    configPath: "service_config.copy.home.process.title",
    label: "Process headline", section: "Process Steps" },

  //   About page section headings
  { testid: "about-story-eyebrow", type: "text",
    copyPath: "about.story.eyebrow",
    configPath: "service_config.copy.about.story.eyebrow",
    label: "About story eyebrow", section: "About story" },
  { testid: "about-story-title",   type: "text",
    copyPath: "about.story.title",
    configPath: "service_config.copy.about.story.title",
    label: "About story headline", section: "About story" },
  { testid: "about-values-eyebrow", type: "text",
    copyPath: "about.values.eyebrow",
    configPath: "service_config.copy.about.values.eyebrow",
    label: "About values eyebrow", section: "About values" },
  { testid: "about-values-title",   type: "text",
    copyPath: "about.values.title",
    configPath: "service_config.copy.about.values.title",
    label: "About values headline", section: "About values" },
  { testid: "about-trust-eyebrow", type: "text",
    copyPath: "about.trust.eyebrow",
    configPath: "service_config.copy.about.trust.eyebrow",
    label: "About trust eyebrow", section: "About trust" },
  { testid: "about-trust-title",   type: "text",
    copyPath: "about.trust.title",
    configPath: "service_config.copy.about.trust.title",
    label: "About trust headline", section: "About trust" },

  // ── Ecommerce template (Iter 75) ────────────────────────────────────
  //   ALL of these use their EXISTING canonical scalar fields — we only
  //   add the missing testids + anchor entries. No copyPath.
  { testid: "featured-eyebrow",   type: "text", path: "featured_eyebrow",
    label: "Featured eyebrow", section: "Featured Products" },
  { testid: "featured-headline",  type: "text", path: "featured_headline",
    label: "Featured headline", section: "Featured Products" },
  { testid: "category-headline",  type: "text", path: "category_headline",
    label: "Category split headline", section: "Categories" },
  { testid: "editorial-eyebrow",  type: "text", path: "editorial_eyebrow",
    label: "Editorial eyebrow", section: "Editorial banner" },
  { testid: "editorial-headline", type: "text", path: "editorial_headline",
    label: "Editorial headline", section: "Editorial banner" },
  { testid: "editorial-headline-line2", type: "text", path: "editorial_headline_line2",
    label: "Editorial headline (line 2)", section: "Editorial banner" },
  { testid: "editorial-body",     type: "text", path: "editorial_body",
    label: "Editorial body", section: "Editorial banner" },
  { testid: "editorial-cta",      type: "text", path: "editorial_cta",
    label: "Editorial CTA label", section: "Editorial banner" },
  { testid: "reviews-eyebrow",    type: "text", path: "reviews_eyebrow",
    label: "Reviews eyebrow", section: "Reviews" },
  { testid: "reviews-headline",   type: "text", path: "reviews_headline",
    label: "Reviews headline", section: "Reviews" },

  // ── Restaurant template (Iter 75) ───────────────────────────────────
  //   Copy-override paths align with the top-level restaurant_config
  //   section blocks (menu_intro, about, contact, reservations).
  { testid: "restaurant-menu-eyebrow", type: "text",
    copyPath: "menu_intro.eyebrow",
    configPath: "restaurant_config.menu_intro.eyebrow",
    label: "Menu eyebrow", section: "Menu" },
  { testid: "restaurant-menu-title",   type: "text",
    copyPath: "menu_intro.title",
    configPath: "restaurant_config.menu_intro.title",
    label: "Menu headline (line 1)", section: "Menu" },
  { testid: "restaurant-menu-title-em", type: "text",
    copyPath: "menu_intro.title_em",
    configPath: "restaurant_config.menu_intro.title_em",
    label: "Menu headline (italic line)", section: "Menu" },
  { testid: "restaurant-about-eyebrow", type: "text",
    copyPath: "about.eyebrow",
    configPath: "restaurant_config.about.eyebrow",
    label: "About eyebrow", section: "About (restaurant)" },
  { testid: "restaurant-about-title",   type: "text",
    copyPath: "about.title",
    configPath: "restaurant_config.about.title",
    label: "About headline (line 1)", section: "About (restaurant)" },
  { testid: "restaurant-about-title-em", type: "text",
    copyPath: "about.title_em",
    configPath: "restaurant_config.about.title_em",
    label: "About headline (italic line)", section: "About (restaurant)" },
  { testid: "restaurant-contact-eyebrow", type: "text",
    copyPath: "contact.eyebrow",
    configPath: "restaurant_config.contact.eyebrow",
    label: "Contact eyebrow", section: "Contact (restaurant)" },
  { testid: "restaurant-contact-title",   type: "text",
    copyPath: "contact.title",
    configPath: "restaurant_config.contact.title",
    label: "Contact headline (line 1)", section: "Contact (restaurant)" },
  { testid: "restaurant-contact-title-em", type: "text",
    copyPath: "contact.title_em",
    configPath: "restaurant_config.contact.title_em",
    label: "Contact headline (italic line)", section: "Contact (restaurant)" },
  { testid: "restaurant-reservations-eyebrow", type: "text",
    copyPath: "reservations.eyebrow",
    configPath: "restaurant_config.reservations.eyebrow",
    label: "Reservations eyebrow", section: "Reservations" },
  { testid: "restaurant-reservations-title", type: "text",
    copyPath: "reservations.title",
    configPath: "restaurant_config.reservations.title",
    label: "Reservations headline (line 1)", section: "Reservations" },
  { testid: "restaurant-reservations-title-em", type: "text",
    copyPath: "reservations.title_em",
    configPath: "restaurant_config.reservations.title_em",
    label: "Reservations headline (italic line)", section: "Reservations" },

  // ─── Iter 77: ABSOLUTE COVERAGE — every visible element editable ─────

  // ── Service: Home consultation bullets ──────────────────────────────
  { testid: "consult-bullet-1", type: "text", copyPath: "home.consultation.bullet1", configPath: "service_config.copy.home.consultation.bullet1", defaultValue: "No-obligation diagnosis at your home", label: "Consultation bullet 1", section: "Consultation" },
  { testid: "consult-bullet-2", type: "text", copyPath: "home.consultation.bullet2", configPath: "service_config.copy.home.consultation.bullet2", defaultValue: "Written, itemized quote — no surprise fees", label: "Consultation bullet 2", section: "Consultation" },
  { testid: "consult-bullet-3", type: "text", copyPath: "home.consultation.bullet3", configPath: "service_config.copy.home.consultation.bullet3", defaultValue: "Same-week scheduling for most services", label: "Consultation bullet 3", section: "Consultation" },
  { testid: "consult-bullet-4", type: "text", copyPath: "home.consultation.bullet4", configPath: "service_config.copy.home.consultation.bullet4", defaultValue: "Talk to a real human, not a call center", label: "Consultation bullet 4", section: "Consultation" },

  // ── Service: forms (all visible form copy via FormCopyEditor) ───────
  { testid: "consultation-form", type: "form_copy", formKey: "service_consultation", label: "Consultation form", section: "Consultation" },
  { testid: "contact-form", type: "form_copy", formKey: "service_contact", label: "Contact form", section: "Contact" },

  // ── Service: Home service-area section ──────────────────────────────
  { testid: "service-area-eyebrow", type: "text", copyPath: "home.serviceArea.eyebrow", configPath: "service_config.copy.home.serviceArea.eyebrow", defaultValue: "Local coverage", label: "Service area eyebrow", section: "Service Area" },
  { testid: "service-area-blurb", type: "text", copyPath: "home.serviceArea.blurb", configPath: "service_config.copy.home.serviceArea.blurb", label: "Service area headline", section: "Service Area" },
  { testid: "service-area-paragraph", type: "text", copyPath: "home.serviceArea.paragraph", configPath: "service_config.copy.home.serviceArea.paragraph", defaultValue: "Not sure if we cover your ZIP code? Send it over — we'll confirm within the hour and get you scheduled.", label: "Service area text", section: "Service Area" },
  { testid: "area-check-availability", type: "button", copyPath: "home.serviceArea.ctaLabel", linkCopyPath: "home.serviceArea.ctaTo", defaultLabel: "Check Availability", defaultLink: "/contact", label: "Availability CTA", section: "Service Area" },
  { testid: "service-area-section", type: "service_areas", path: null, label: "Service areas", section: "Service Area" },

  // ── Service: Home overview / testimonials buttons ───────────────────
  { testid: "services-view-all", type: "button", copyPath: "home.servicesOverview.viewAllLabel", linkCopyPath: "home.servicesOverview.viewAllTo", defaultLabel: "View all services", defaultLink: "/services", label: "View-all link", section: "Services Overview" },
  { testid: "services-cta", type: "button", copyPath: "home.servicesOverview.quoteCtaLabel", linkCopyPath: "home.servicesOverview.quoteCtaTo", defaultLabel: "Request a Free Quote", defaultLink: "/consultations", label: "Quote CTA", section: "Services Overview" },
  { testid: "testimonials-view-all", type: "button", copyPath: "home.testimonials.viewAllLabel", linkCopyPath: "home.testimonials.viewAllTo", defaultLabel: "Read all reviews", defaultLink: "/reviews", label: "Reviews link", section: "Testimonials" },

  // ── Service: CTA banner (page-scoped via resolveAnchorForPage) ──────
  { testid: "cta-eyebrow", type: "text", copyPath: "home.finalCta.eyebrow", configPath: "service_config.copy.home.finalCta.eyebrow", defaultValue: "Ready when you are", label: "Final CTA eyebrow", section: "Call to action" },
  { testid: "cta-primary", type: "button", copyPath: "home.finalCta.primaryLabel", linkCopyPath: "home.finalCta.primaryTo", defaultLabel: "Book a Consultation", defaultLink: "/consultations", label: "CTA primary button", section: "Call to action" },
  { testid: "cta-secondary", type: "button", copyPath: "home.finalCta.secondaryLabel", linkCopyPath: "home.finalCta.secondaryTo", defaultLabel: "Request a Quote", defaultLink: "/contact", label: "CTA secondary button", section: "Call to action" },
  { testid: "cta-call", type: "text", copyPath: "shared.ctaCallPrefix", configPath: "service_config.copy.shared.ctaCallPrefix", defaultValue: "or call", label: "CTA call prefix", section: "Call to action" },
  { testid: "cta-image", type: "image", path: "cta_image_url", label: "CTA background image", section: "Call to action" },

  // ── Service: hero extras ─────────────────────────────────────────────
  { testid: "hero-trust-line", type: "text", copyPath: "home.hero.trustLine", configPath: "service_config.copy.home.hero.trustLine", label: "Hero trust line", section: "Hero" },
  { testid: "hero-call-cta", type: "text", path: "contact_phone", label: "Phone number", section: "Hero" },

  // ── Service: About page ──────────────────────────────────────────────
  { testid: "about-story-text", type: "text", path: "about", label: "About story text", section: "About story" },
  { testid: "about-values-items", type: "about_values", path: null, label: "Value cards", section: "About values" },
  { testid: "about-stats", type: "about_stats", path: null, label: "Trust stats", section: "About trust" },
  { testid: "about-trust-image", type: "image", path: "why_choose_us_image_url", label: "About photo", section: "About trust" },
  { testid: "about-book-consult", type: "button", copyPath: "about.story.ctaLabel", linkCopyPath: "about.story.ctaTo", defaultLabel: "Book a Consultation", defaultLink: "/consultations", label: "About CTA", section: "About trust" },

  // ── Service: Contact page ────────────────────────────────────────────
  { testid: "contact-section-eyebrow", type: "text", copyPath: "contact.section.eyebrow", configPath: "service_config.copy.contact.section.eyebrow", defaultValue: "Reach us", label: "Contact eyebrow", section: "Contact" },
  { testid: "contact-section-title", type: "text", copyPath: "contact.section.title", configPath: "service_config.copy.contact.section.title", defaultValue: "Reach us any way that works.", label: "Contact headline", section: "Contact" },
  { testid: "contact-label-phone", type: "text", copyPath: "contact.info.phoneLabel", configPath: "service_config.copy.contact.info.phoneLabel", defaultValue: "Phone", label: "Phone label", section: "Contact" },
  { testid: "contact-label-email", type: "text", copyPath: "contact.info.emailLabel", configPath: "service_config.copy.contact.info.emailLabel", defaultValue: "Email", label: "Email label", section: "Contact" },
  { testid: "contact-label-office", type: "text", copyPath: "contact.info.officeLabel", configPath: "service_config.copy.contact.info.officeLabel", defaultValue: "Office", label: "Office label", section: "Contact" },
  { testid: "contact-info-phone", type: "text", path: "contact_phone", label: "Phone number", section: "Contact" },
  { testid: "contact-info-email", type: "text", path: "contact_email", label: "Email address", section: "Contact" },
  { testid: "contact-info-address", type: "text", path: "contact_address", label: "Address", section: "Contact" },
  { testid: "map-placeholder", type: "text", path: "contact_address", label: "Address", section: "Contact" },
  { testid: "business-hours-heading", type: "text", copyPath: "contact.hours.heading", configPath: "service_config.copy.contact.hours.heading", defaultValue: "Business Hours", label: "Hours heading", section: "Contact" },
  { testid: "business-hours-note", type: "text", copyPath: "contact.hours.note", configPath: "service_config.copy.contact.hours.note", defaultValue: "24/7 emergency line available for burst pipes, no power, no heat.", label: "Hours note", section: "Contact" },
  { testid: "business-hours", type: "business_hours", path: null, label: "Business hours", section: "Contact" },

  // ── Service: Consultations + Reviews pages ───────────────────────────
  { testid: "consult-expect-eyebrow", type: "text", copyPath: "consultations.expect.eyebrow", configPath: "service_config.copy.consultations.expect.eyebrow", defaultValue: "What to expect", label: "Expect eyebrow", section: "Consultation" },
  { testid: "consult-expect-title", type: "text", copyPath: "consultations.expect.title", configPath: "service_config.copy.consultations.expect.title", defaultValue: "What happens during a consultation?", label: "Expect headline", section: "Consultation" },
  { testid: "consult-expect-subtitle", type: "text", copyPath: "consultations.expect.subtitle", configPath: "service_config.copy.consultations.expect.subtitle", defaultValue: "Consultations take about 30 minutes and are always free. We'll come to your home (or hop on a quick call if you prefer) and walk you through your options with zero pressure.", label: "Expect subhead", section: "Consultation" },
  { testid: "reviews-section-eyebrow", type: "text", copyPath: "reviews.section.eyebrow", configPath: "service_config.copy.reviews.section.eyebrow", defaultValue: "What locals are saying", label: "Reviews eyebrow", section: "Reviews" },
  { testid: "reviews-section-title", type: "text", copyPath: "reviews.section.title", configPath: "service_config.copy.reviews.section.title", defaultValue: "Trusted by 300+ households across the region.", label: "Reviews headline", section: "Reviews" },
  { testid: "reviews-trust-stats", type: "form_copy", formKey: "service_reviews_stats", label: "Trust stats", section: "Reviews" },

  // ── Ecommerce: hero extras ───────────────────────────────────────────
  { testid: "hero-subhead", type: "text", path: "hero_subtitle", label: "Hero subhead", section: "Hero" },
  { testid: "hero-headline-2", type: "text", copyPath: "hero.headline2", configPath: "ecommerce_config.hero.headline2", label: "Hero headline (line 2)", section: "Hero" },
  { testid: "hero-headline-3", type: "text", copyPath: "hero.headline3", configPath: "ecommerce_config.hero.headline3", label: "Hero headline (line 3)", section: "Hero" },

  // ── Ecommerce: home extras ───────────────────────────────────────────
  { testid: "view-all-link", type: "button", copyPath: "home.viewAllLabel", linkCopyPath: "home.viewAllTo", defaultLabel: "View all", defaultLink: "/shop", label: "View-all link", section: "Featured Products" },
  { testid: "reviews-band", type: "testimonials", path: null, label: "Customer reviews", section: "Reviews" },
  { testid: "reviews-average", type: "text", copyPath: "home.reviewsAverageLine", configPath: "ecommerce_config.copy.home.reviewsAverageLine", defaultValue: "★ 4.8 / 5 average", label: "Average rating line", section: "Reviews" },
  { testid: "newsletter-eyebrow", type: "text", copyPath: "newsletter.eyebrow", configPath: "ecommerce_config.copy.newsletter.eyebrow", label: "Newsletter eyebrow", section: "Newsletter" },
  { testid: "newsletter-headline-1", type: "text", copyPath: "newsletter.headline1", configPath: "ecommerce_config.copy.newsletter.headline1", label: "Newsletter headline (line 1)", section: "Newsletter" },
  { testid: "newsletter-headline-2", type: "text", copyPath: "newsletter.headline2", configPath: "ecommerce_config.copy.newsletter.headline2", label: "Newsletter headline (line 2)", section: "Newsletter" },
  { testid: "newsletter-subhead", type: "text", copyPath: "newsletter.subhead", configPath: "ecommerce_config.copy.newsletter.subhead", label: "Newsletter subhead", section: "Newsletter" },
  { testid: "newsletter-fineprint", type: "text", copyPath: "newsletter.finePrint", configPath: "ecommerce_config.copy.newsletter.finePrint", label: "Newsletter fine print", section: "Newsletter" },
  { testid: "newsletter", type: "form_copy", formKey: "ecom_newsletter", label: "Newsletter form", section: "Newsletter" },

  // ── Ecommerce: shop page ─────────────────────────────────────────────
  { testid: "shop-eyebrow", type: "text", copyPath: "shop.eyebrow", configPath: "ecommerce_config.copy.shop.eyebrow", defaultValue: "— Shop", label: "Shop eyebrow", section: "Shop" },
  { testid: "shop-title", type: "text", copyPath: "shop.title", configPath: "ecommerce_config.copy.shop.title", defaultValue: "Shop all.", label: "Shop headline", section: "Shop" },
  { testid: "shop-toolbar", type: "form_copy", formKey: "ecom_shop_toolbar", label: "Shop filters & sort", section: "Shop" },
  { testid: "category-tiles", type: "category_tiles", path: null, label: "Category tiles", section: "Categories" },

  // ── Restaurant ───────────────────────────────────────────────────────
  { testid: "hero-reserve-btn", type: "button", copyPath: "site.hero_reserve_cta", configPath: "restaurant_config.site.hero_reserve_cta", defaultLabel: "Reserve a Table", noLink: true, label: "Reserve button", section: "Hero" },
  { testid: "hero-menu-btn", type: "button", copyPath: "site.hero_menu_cta", configPath: "restaurant_config.site.hero_menu_cta", defaultLabel: "View the Menu", noLink: true, label: "Menu button", section: "Hero" },
  { testid: "hero-scroll-cue", type: "text", copyPath: "site.scroll_label", configPath: "restaurant_config.site.scroll_label", defaultValue: "Scroll", label: "Scroll cue", section: "Hero" },
  { testid: "hero-top-right-1", type: "text", copyPath: "site.hero_top_right_1", configPath: "restaurant_config.site.hero_top_right_1", label: "Hero corner line 1", section: "Hero" },
  { testid: "hero-top-right-2", type: "text", copyPath: "site.hero_top_right_2", configPath: "restaurant_config.site.hero_top_right_2", label: "Hero corner line 2", section: "Hero" },
  { testid: "restaurant-hero-stats", type: "hero_stats", path: null, label: "Info strip", section: "Hero" },
  { testid: "restaurant-menu-blurb", type: "text", copyPath: "menu_intro.blurb", configPath: "restaurant_config.menu_intro.blurb", label: "Menu intro text", section: "Menu" },
  { testid: "menu-price", type: "text", path: "tasting_menu_price", label: "Menu price line", section: "Menu" },
  { testid: "reservation-info", type: "reservation_info", path: null, label: "Reservation details", section: "Reservations" },
  { testid: "reservation-form", type: "form_copy", formKey: "restaurant_reservation", label: "Reservation form", section: "Reservations" },
  { testid: "restaurant-about-text", type: "text", path: "about", label: "About text", section: "About (restaurant)" },
  { testid: "about-image-1", type: "image", path: "about_image_1", label: "About image 1", section: "About (restaurant)" },
  { testid: "about-image-2", type: "image", path: "about_image_2", label: "About image 2", section: "About (restaurant)" },
  { testid: "restaurant-contact-info", type: "restaurant_contact", path: null, label: "Contact details", section: "Contact (restaurant)" },
  { testid: "footer-appointment-note", type: "text", copyPath: "footer.appointment_note", configPath: "restaurant_config.footer.appointment_note", defaultValue: "Reservations by appointment", label: "Footer note", section: "Footer" },

  // ── Ecommerce: SRC sub-pages (Iter 77) ──────────────────────────────
  { testid: "about-page-eyebrow", type: "text", copyPath: "pages.about.eyebrow", configPath: "ecommerce_config.copy.pages.about.eyebrow", label: "About eyebrow", section: "About page" },
  { testid: "about-page-title", type: "text", copyPath: "pages.about.title", configPath: "ecommerce_config.copy.pages.about.title", label: "About headline", section: "About page" },
  { testid: "about-page-body", type: "text", path: "about", label: "About text", section: "About page" },
  { testid: "contact-page-eyebrow", type: "text", copyPath: "pages.contact.eyebrow", configPath: "ecommerce_config.copy.pages.contact.eyebrow", label: "Contact eyebrow", section: "Contact page" },
  { testid: "contact-page-title", type: "text", copyPath: "pages.contact.title", configPath: "ecommerce_config.copy.pages.contact.title", label: "Contact headline", section: "Contact page" },
  { testid: "faq-page-eyebrow", type: "text", copyPath: "pages.faq.eyebrow", configPath: "ecommerce_config.copy.pages.faq.eyebrow", label: "FAQ eyebrow", section: "FAQ page" },
  { testid: "faq-page-title", type: "text", copyPath: "pages.faq.title", configPath: "ecommerce_config.copy.pages.faq.title", label: "FAQ headline", section: "FAQ page" },
  { testid: "best-page-eyebrow", type: "text", copyPath: "pages.bestSellers.eyebrow", configPath: "ecommerce_config.copy.pages.bestSellers.eyebrow", label: "Best sellers eyebrow", section: "Best sellers page" },
  { testid: "best-page-title", type: "text", copyPath: "pages.bestSellers.title", configPath: "ecommerce_config.copy.pages.bestSellers.title", label: "Best sellers headline", section: "Best sellers page" },
];

/**
 * Iter 77 — prefix anchors. Matched when no exact testid entry exists and
 * the element's testid starts with `testidPrefix`. Order matters: first
 * match wins, so more-specific prefixes come first.
 */
export const PREFIX_ANCHORS = [
  { testidPrefix: "service-detail-quote-", type: "button", copyPath: "services.detail.quoteLabel", linkCopyPath: "services.detail.quoteTo", defaultLabel: "Request Quote", defaultLink: "/consultations", label: "Quote button", section: "Services" },
  { testidPrefix: "service-detail-book-", type: "button", copyPath: "services.detail.bookLabel", linkCopyPath: "services.detail.bookTo", defaultLabel: "Schedule Service", defaultLink: "/consultations", label: "Schedule button", section: "Services" },
  { testidPrefix: "service-detail-", type: "services", path: null, label: "Services list", section: "Services" },
  { testidPrefix: "product-card-", type: "products", path: null, label: "Product grid", section: "Products" },
  { testidPrefix: "category-tile-", type: "category_tiles", path: null, label: "Category tiles", section: "Categories" },
  { testidPrefix: "filter-cat-", type: "form_copy", formKey: "ecom_shop_toolbar", label: "Shop filters & sort", section: "Shop" },
  { testidPrefix: "expect-", type: "form_copy", formKey: "service_consult_expect", label: "What to expect", section: "Consultation" },
  { testidPrefix: "stat-", type: "hero_stats", path: null, label: "Info strip", section: "Hero" },
  { testidPrefix: "area-", type: "service_areas", path: null, label: "Service areas", section: "Service Area" },
  // Ecommerce policy pages — resolved per-slug in resolveAnchorForPage.
  { testidPrefix: "policy-eyebrow-", type: "text", label: "Policy eyebrow", section: "Policy page" },
  { testidPrefix: "policy-title-", type: "text", label: "Policy title", section: "Policy page" },
  { testidPrefix: "policy-body-", type: "text", label: "Policy text", section: "Policy page" },
];

/**
 * Iter 77 — page-aware anchor resolution. The service template reuses the
 * same <Hero> and <CTABanner> testids on every page, but each page's copy
 * lives at `copy.<page>.hero` / `copy.<page>.finalCta`. Resolving here
 * keeps a single canonical write path per visible element (no wrong-target
 * writes: clicking the FAQ hero edits the FAQ hero, not Home's).
 */
const SERVICE_HERO_CTA_DEFAULTS = {
  about:    { primary: ["Book a Consultation", "/consultations"], secondary: ["See Our Work", "/services"] },
  services: { primary: ["Request a Quote", "/consultations"], secondary: ["Contact Us", "/contact"] },
  reviews:  { primary: ["Request a Quote", "/consultations"], secondary: ["Book a Consultation", "/consultations"] },
  faq:      { primary: ["Book a Consultation", "/consultations"], secondary: ["Contact Us", "/contact"] },
  contact:  { primary: ["Book a Consultation", "/consultations"], secondary: ["Call Now", "tel:"] },
  consultations: { primary: ["Book a Consultation", "/consultations"], secondary: ["Contact Us", "/contact"] },
};
const SERVICE_FINALCTA_DEFAULTS = {
  home:          { eyebrow: "Ready when you are", primary: ["Book a Consultation", "/consultations"], secondary: ["Request a Quote", "/contact"] },
  services:      { eyebrow: "Ready when you are", primary: ["Book a Consultation", "/consultations"], secondary: ["Call Now", "/contact"] },
  about:         { eyebrow: "Ready when you are", primary: ["Book a Consultation", "/consultations"], secondary: ["Request a Quote", "/contact"] },
  consultations: { eyebrow: "Prefer to talk first?", primary: ["Call Now", "/contact"], secondary: ["Contact Us", "/contact"] },
  reviews:       { eyebrow: "Ready when you are", primary: ["Book a Consultation", "/consultations"], secondary: ["Request a Quote", "/contact"] },
  faq:           { eyebrow: "Ready when you are", primary: ["Contact Us", "/contact"], secondary: ["Book a Consultation", "/consultations"] },
  contact:       { eyebrow: "Ready when you are", primary: ["Book a Consultation", "/consultations"], secondary: ["Get Started", "/services"] },
};
const SERVICE_PAGE_HERO_IMAGE_KEY = {
  home: "hero_image_url",
  services: "services_hero_image_url",
  about: "about_hero_image_url",
  consultations: "consultations_hero_image_url",
  reviews: "reviews_hero_image_url",
  faq: "faq_hero_image_url",
  contact: "contact_hero_image_url",
};

export function resolveAnchorForPage(anchor, template, pageSlug) {
  if (!anchor || template !== "service") return anchor;
  const page = pageSlug || "home";
  const t = anchor.testid || "";

  // Per-page hero background image → per-page canonical scalar.
  if (t === "hero-image") {
    const key = SERVICE_PAGE_HERO_IMAGE_KEY[page] || "hero_image_url";
    return { ...anchor, path: key, label: page === "home" ? "Hero image" : `${cap(page)} hero image` };
  }

  if (page === "home") {
    if (t === "hero-primary-cta") {
      return { ...anchor, type: "button", path: "hero_cta_text", linkPath: "hero_cta_link", defaultLabel: "Book a Consultation", defaultLink: "/consultations" };
    }
    if (t === "hero-secondary-cta") {
      return { ...anchor, type: "button", path: "hero_cta2_text", linkPath: "hero_cta2_link", defaultLabel: "View Services", defaultLink: "/services" };
    }
    return anchor;
  }

  // Sub-pages: hero text/CTAs come from copy.<page>.hero.*
  const heroText = { "hero-eyebrow": "eyebrow", "hero-title": "title", "hero-subtitle": "subtitle", "hero-trust-line": "trustLine" };
  if (heroText[t]) {
    return {
      ...anchor, type: "text", path: undefined, linkPath: undefined,
      copyPath: `${page}.hero.${heroText[t]}`,
      configPath: `service_config.copy.${page}.hero.${heroText[t]}`,
      label: `${cap(page)} ${anchor.label.toLowerCase()}`,
    };
  }
  const ctaDef = SERVICE_HERO_CTA_DEFAULTS[page] || SERVICE_HERO_CTA_DEFAULTS.about;
  if (t === "hero-primary-cta" || t === "hero-secondary-cta") {
    const which = t === "hero-primary-cta" ? "primary" : "secondary";
    return {
      ...anchor, type: "button", path: undefined, linkPath: undefined,
      copyPath: `${page}.hero.${which}CtaLabel`,
      linkCopyPath: `${page}.hero.${which}CtaTo`,
      defaultLabel: ctaDef[which][0], defaultLink: ctaDef[which][1],
      label: `${cap(page)} hero ${which} CTA`,
    };
  }
  const fc = SERVICE_FINALCTA_DEFAULTS[page] || SERVICE_FINALCTA_DEFAULTS.home;
  const fcMap = {
    "cta-title":    { key: "title", type: "text" },
    "cta-subtitle": { key: "subtitle", type: "text" },
    "cta-eyebrow":  { key: "eyebrow", type: "text", defaultValue: fc.eyebrow },
    "cta-primary":  { key: "primaryLabel", type: "button", linkKey: "primaryTo", defaults: fc.primary },
    "cta-secondary": { key: "secondaryLabel", type: "button", linkKey: "secondaryTo", defaults: fc.secondary },
  };
  if (fcMap[t]) {
    const m = fcMap[t];
    const base = {
      ...anchor, type: m.type, path: undefined, linkPath: undefined,
      copyPath: `${page}.finalCta.${m.key}`,
      configPath: `service_config.copy.${page}.finalCta.${m.key}`,
      label: `${cap(page)} ${anchor.label.toLowerCase()}`,
    };
    if (m.defaultValue) base.defaultValue = m.defaultValue;
    if (m.linkKey) {
      base.linkCopyPath = `${page}.finalCta.${m.linkKey}`;
      base.defaultLabel = m.defaults[0];
      base.defaultLink = m.defaults[1];
      delete base.configPath;
    }
    return base;
  }
  return anchor;
}

function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

/**
 * Given a DOM element (and optionally the raw click coordinates), find the
 * best editable anchor for the click. We walk up from `target` element by
 * element and return the first ancestor whose `data-testid` matches an
 * entry in EDITABLE_ANCHORS.
 */
export function findEditableAnchor(target, clientX = null, clientY = null) {
  if (!target || target.nodeType !== 1) return null;
  const byTestid = new Map(EDITABLE_ANCHORS.map((a) => [a.testid, a]));

  // Walk ancestors from `target` upward.
  let ancestorHit = null;
  let el = target;
  while (el && el !== document.body && el !== document.documentElement) {
    const tid = el.getAttribute?.("data-testid");
    if (tid && byTestid.has(tid)) {
      ancestorHit = { anchor: byTestid.get(tid), element: el };
      break;
    }
    el = el.parentElement;
  }

  return ancestorHit;
}

/**
 * Standardized "section outline" for each SRC template — this drives the
 * left sidebar's Section Outline. Each entry has a label + a testid or
 * DOM selector we can scrollIntoView on click. Section reorder / duplicate
 * / delete are Phase 2/3 (see PRD.md).
 */
export const SRC_SECTION_OUTLINES = {
  service: [
    { label: "Header",         testid: "site-header" },
    { label: "Hero",           testid: "hero-title" },
    { label: "Trust Bar",      testid: "trust-bar" },
    { label: "Services",       testid: "services-section" },
    { label: "Why Choose Us",  testid: "why-choose-us" },
    { label: "Process Steps",  testid: "process-section" },
    { label: "Testimonials",   testid: "testimonials-section" },
    { label: "FAQ",            testid: "faq-section" },
    { label: "Footer",         testid: "site-footer" },
  ],
  ecommerce: [
    { label: "Announcement Bar", testid: "announcement-bar" },
    { label: "Header",           testid: "site-header" },
    { label: "Hero",             testid: "hero-headline" },
    { label: "Trust Bar",        testid: "trust-bar" },
    { label: "Featured Products", testid: "featured-products" },
    { label: "Newsletter",       testid: "newsletter" },
    { label: "Footer",           testid: "site-footer" },
  ],
  restaurant: [
    { label: "Header",           testid: "site-header" },
    { label: "Hero",             testid: "restaurant-hero-title" },
    { label: "Menu Preview",     testid: "menu-preview" },
    { label: "Reservations",     testid: "reservations-section" },
    { label: "About",            testid: "about-section" },
    { label: "Gallery",          testid: "gallery-section" },
    { label: "Contact",          testid: "contact-section" },
  ],
};
